import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash, ArrowLeft, FloppyDisk, CaretRight, XCircle } from "@phosphor-icons/react";

const RATING_OPTIONS = [
  { v: "default", label: "Padrão da empresa" },
  { v: "enabled", label: "Ativado" },
  { v: "disabled", label: "Desativado" },
];

function emptyForm() {
  return { id: null, title: "", close_message: "", rating_override: "default" };
}

export default function CompanyClosureReasons() {
  const [list, setList] = useState([]);
  const [search, setSearch] = useState("");
  const [mode, setMode] = useState("list");
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/closure-reasons");
      setList(data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(emptyForm()); setMode("form"); };
  const openEdit = (r) => { setForm({ ...r, close_message: r.close_message || "" }); setMode("form"); };
  const backToList = () => { setMode("list"); setForm(emptyForm()); };

  const save = async () => {
    if (!form.title.trim()) return toast.error("Título obrigatório");
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        close_message: form.close_message.trim() || null,
        rating_override: form.rating_override,
      };
      if (form.id) {
        await api.put(`/closure-reasons/${form.id}`, payload);
        toast.success("Motivo atualizado");
      } else {
        await api.post("/closure-reasons", payload);
        toast.success("Motivo criado");
      }
      backToList();
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!form.id) return;
    if (!window.confirm(`Excluir motivo "${form.title}"?`)) return;
    try {
      await api.delete(`/closure-reasons/${form.id}`);
      toast.success("Motivo removido");
      backToList();
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const filtered = list.filter((x) => x.title.toLowerCase().includes(search.toLowerCase()));

  if (mode === "form") {
    return (
      <div className="p-8 max-w-3xl">
        <div className="mb-6">
          <p className="text-xs font-semibold tracking-[0.3em] uppercase text-[#0F4C3A]">Atendimento</p>
          <h1 className="text-4xl font-black tracking-tighter text-[#1A1A1A] mt-1">Motivos de finalização</h1>
          <p className="text-sm text-[#5C5C59] mt-2">
            Motivos de finalização permitem organizar e categorizar sessões finalizadas de forma eficiente.
          </p>
        </div>

        <div className="flex items-center justify-between mb-4">
          <button onClick={backToList} data-testid="cr-back-btn" className="flex items-center gap-1.5 text-sm text-[#5C5C59] hover:text-[#0F4C3A] transition">
            <ArrowLeft size={16} weight="bold" /> voltar
          </button>
          <div className="flex items-center gap-2">
            <Button onClick={save} disabled={saving} data-testid="cr-save-btn" className="bg-[#0F4C3A] hover:bg-[#0A3629] text-white rounded-none shadow-brutal">
              <FloppyDisk size={16} weight="bold" className="mr-1.5" />
              {saving ? "Salvando…" : "Salvar"}
            </Button>
            {form.id && (
              <Button onClick={remove} data-testid="cr-delete-btn" className="bg-[#DC2626] hover:bg-[#B91C1C] text-white rounded-none shadow-brutal">
                <Trash size={16} weight="bold" />
              </Button>
            )}
          </div>
        </div>

        <div className="bg-white border border-[#E5E5E0] shadow-brutal p-6 space-y-5">
          <div>
            <Label className="text-sm">Título do motivo de finalização</Label>
            <Input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              data-testid="cr-title-input"
              className="rounded-none mt-1"
              placeholder="Ex: Dúvida, Engano, Rejeitado, Resolvido"
            />
          </div>
          <div>
            <Label className="text-sm">Mensagem de finalização</Label>
            <p className="text-[10px] text-[#5C5C59] mt-0.5">
              Opcional. Mensagem enviada sempre que um atendimento for finalizado com este motivo.
            </p>
            <textarea
              value={form.close_message}
              onChange={(e) => setForm({ ...form, close_message: e.target.value })}
              data-testid="cr-message-input"
              className="w-full mt-1 border border-[#E5E5E0] p-3 text-sm min-h-[100px]"
              placeholder="Ex: Atendimento finalizado. Obrigado pelo contato!"
            />
          </div>
          <div>
            <Label className="text-sm">Avaliação de atendimento</Label>
            <p className="text-[10px] text-[#5C5C59] mt-0.5">
              Se pergunta de nota (1-5) deve ser enviada quando este motivo for usado.
            </p>
            <Select value={form.rating_override} onValueChange={(v) => setForm({ ...form, rating_override: v })}>
              <SelectTrigger data-testid="cr-rating-select" className="rounded-none mt-1 bg-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white rounded-none">
                {RATING_OPTIONS.map((o) => <SelectItem key={o.v} value={o.v}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-4xl">
      <div className="mb-8">
        <p className="text-xs font-semibold tracking-[0.3em] uppercase text-[#0F4C3A]">Atendimento</p>
        <h1 className="text-4xl font-black tracking-tighter text-[#1A1A1A] mt-1">Motivos de finalização</h1>
        <p className="text-sm text-[#5C5C59] mt-2">
          Motivos de finalização permitem organizar e categorizar sessões finalizadas de forma eficiente.
        </p>
      </div>

      <div className="flex items-center gap-3 mb-4">
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Pesquisar…" data-testid="cr-search-input" className="rounded-none max-w-md" />
        <Button onClick={openNew} data-testid="cr-add-btn" className="bg-[#0F4C3A] hover:bg-[#0A3629] text-white rounded-none shadow-brutal">
          <Plus size={16} weight="bold" className="mr-1.5" /> Adicionar
        </Button>
      </div>

      <div className="bg-white border border-[#E5E5E0] shadow-brutal" data-testid="cr-list">
        <div className="grid grid-cols-[1fr_auto] items-center px-4 py-2 bg-[#F7F7F5] border-b border-[#E5E5E0] text-[10px] uppercase tracking-widest font-semibold text-[#5C5C59]">
          <div>Título</div>
          <div>Ação</div>
        </div>
        {filtered.length === 0 && (
          <div className="p-6 text-center text-xs text-[#5C5C59]">Nenhum motivo cadastrado.</div>
        )}
        {filtered.map((r) => (
          <button
            key={r.id}
            onClick={() => openEdit(r)}
            data-testid={`cr-row-${r.id}`}
            className="w-full grid grid-cols-[1fr_auto] items-center gap-3 px-4 py-3 border-b border-[#E5E5E0] last:border-b-0 hover:bg-[#F7F7F5] transition text-left"
          >
            <div>
              <div className="font-semibold text-sm">{r.title}</div>
              {r.close_message && <div className="text-[11px] text-[#5C5C59] mt-0.5 truncate max-w-lg">{r.close_message}</div>}
            </div>
            <div className="text-xs text-[#0F4C3A] font-semibold flex items-center gap-1">
              Editar <CaretRight size={12} weight="bold" />
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
